<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ScoreResult } from '@/features/scoring/lib/types'
import { useScoring } from '@/features/scoring/composables/useScoring'
import { buildScoreResult, isScoreInputsComplete } from '@/features/scoring/lib/buildScoreResult'
import RecordTestSheet from '@/features/scoring/components/RecordTestSheet.vue'
import type { MemberCompletionState, TestSessionDraft, UFPMMember } from '../lib/types'
import {
  draftToScoringState,
  hasDraftContent,
  scoringStateToDraft,
} from '../lib/scoreEntryToDraft'
import { scoreEntryToResult } from '../lib/scoreEntryToResult'
import { buildScoreHistoryEntry } from '../lib/scoreHistoryEntry'

const props = withDefaults(defineProps<{
  member: UFPMMember
  draft?: TestSessionDraft
  completion?: MemberCompletionState
}>(), {
  completion: 'empty',
})

const emit = defineEmits<{
  saved: [memberId: string, score: ScoreResult]
  'update-draft': [memberId: string, draft: TestSessionDraft]
  'clear-draft': [memberId: string]
}>()

const { profile, pushUps, core, cardio, whtr, result, componentScores, clear } = useScoring({
  autoSave: false,
  setGlobalScore: false,
  restorePersistedInputs: false,
})

const isSheetOpen = ref(false)
const isSaving = ref(false)
const savedLocally = ref(false)

const anyExempt = computed(() => pushUps.exempt || core.exempt || cardio.exempt || whtr.exempt)

const liveResult = computed(() =>
  buildScoreResult({ profile, pushUps, core, cardio, whtr })
)

const canSave = computed(() =>
  props.completion !== 'saved' &&
  !isSaving.value &&
  isScoreInputsComplete({ profile, pushUps, core, cardio, whtr })
)

const displayResult = computed(() => result.value ?? liveResult.value)

function todayISO(): string {
  const d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function applyMemberProfile() {
  if (props.member.sex === 'male' || props.member.sex === 'female') profile.sex = props.member.sex
}

function applyDraft(draft?: TestSessionDraft) {
  if (!draft) return
  const state = draftToScoringState(draft)
  Object.assign(profile, state.profile)
  Object.assign(pushUps, state.pushUps)
  Object.assign(core, state.core)
  Object.assign(cardio, state.cardio)
  Object.assign(whtr, state.whtr)
}

function syncDraftToSession() {
  if (props.completion === 'saved' || savedLocally.value) return
  const draft = scoringStateToDraft({ profile, pushUps, core, cardio, whtr })
  if (hasDraftContent(draft)) emit('update-draft', props.member.id, draft)
  else emit('clear-draft', props.member.id)
}

async function handleSave() {
  if (!canSave.value || props.completion === 'saved' || isSaving.value) return
  const scored = buildScoreResult({ profile, pushUps, core, cardio, whtr })
  if (!scored) {
    window.showToast?.('Enter a valid age from 17 to 70.')
    return
  }

  isSaving.value = true
  result.value = scored

  const entry = buildScoreHistoryEntry(scored, {
    id: Date.now(),
    recordedDate: todayISO(),
    memberId: props.member.id,
    memberName: props.member.fullName,
  }, { pushUps, core, cardio, whtr })

  window.ufpmTools?.selectMember?.(props.member.id)
  if (window.ufpmTools?.saveScoreForSelectedMember(entry)) {
    savedLocally.value = true
    window.showToast?.(`Score ${scored.composite.toFixed(1)} saved for ${props.member.fullName}`)
    emit('saved', props.member.id, scored)
    isSheetOpen.value = false
  } else {
    window.showToast?.('Could not save score for this member.')
  }
  isSaving.value = false
}

function handleClear() {
  clear()
  applyMemberProfile()
  result.value = null
  emit('clear-draft', props.member.id)
}

onMounted(() => {
  applyMemberProfile()
  if (props.completion === 'saved') {
    const latest = props.member.history?.[0]
    const restored = latest ? scoreEntryToResult(latest, props.member) : null
    if (restored) result.value = restored
  } else {
    applyDraft(props.draft)
  }
})

watch(() => props.completion, (state) => {
  if (state !== 'saved') savedLocally.value = false
})

watch(
  () => [profile, pushUps, core, cardio, whtr] as const,
  () => syncDraftToSession(),
  { deep: true },
)

onBeforeUnmount(() => syncDraftToSession())
</script>

<template>
  <div class="ufpm-test-panel">
    <div class="card">
      <div class="card-title">Administer Test</div>
      <div class="ufpm-workflow-rows">
        <div class="ufpm-workflow-row">
          <span class="ufpm-workflow-label">Member</span>
          <span class="ufpm-workflow-value">{{ member.fullName || 'Selected Member' }}</span>
        </div>
        <div class="ufpm-workflow-row">
          <span class="ufpm-workflow-label">Bracket</span>
          <span class="ufpm-workflow-value">{{ profile.sex || '-' }} / Age {{ profile.age || '-' }}</span>
        </div>
      </div>

      <div v-if="liveResult" class="ufpm-live-score">
        <span class="ufpm-live-score__label">Live composite</span>
        <span class="ufpm-live-score__value">{{ liveResult.composite.toFixed(1) }}</span>
        <span class="ufpm-live-score__cat">{{ liveResult.cat }}</span>
      </div>

      <button
        class="btn btn-primary"
        type="button"
        :disabled="completion === 'saved'"
        @click="isSheetOpen = true"
      >
        {{ completion === 'saved' ? 'Score Saved' : 'Record Test' }}
      </button>
      <p class="ufpm-empty" style="margin-top:8px;">
        Scores saved here attach only to this UFPM member and do not appear in the individual History tab.
      </p>
    </div>

    <div v-if="displayResult && completion === 'saved'" class="card ufpm-test-result">
      <div class="card-title">Saved Score</div>
      <div class="ufpm-profile-id-row">
        <span class="ufpm-profile-score-chip">{{ displayResult.composite.toFixed(1) }}</span>
        <span class="ufpm-profile-name">{{ displayResult.cat }}</span>
      </div>
      <div class="ufpm-hcl-breakdown">
        PU: {{ displayResult.puEx ? 'EXM' : displayResult.puScore.toFixed(1) }} |
        Core: {{ displayResult.coreEx ? 'EXM' : displayResult.coreScore.toFixed(1) }} |
        Cardio: {{ displayResult.cardioEx ? 'EXM' : displayResult.cardioScore.toFixed(1) }} |
        WHtR: {{ displayResult.whtrEx ? 'EXM' : displayResult.whtrScore.toFixed(1) }}
      </div>
    </div>

    <RecordTestSheet
      v-if="isSheetOpen"
      :profile="profile"
      :push-ups="pushUps"
      :core="core"
      :cardio="cardio"
      :whtr="whtr"
      :component-scores="componentScores"
      :any-exempt="anyExempt"
      :ufpm-member-name="member.fullName"
      :ufpm-member-rank="member.rank"
      hide-calculate
      :live-composite="liveResult?.composite ?? null"
      :live-category="liveResult?.cat ?? null"
      :save-disabled="!canSave"
      @close="isSheetOpen = false"
      @save="handleSave"
      @clear="handleClear"
    />
  </div>
</template>

<style scoped>
.ufpm-live-score {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 12px 0;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
}
.ufpm-live-score__label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted, var(--text2));
}
.ufpm-live-score__value {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
}
.ufpm-live-score__cat {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}
</style>